import type { BeforeSendHookPayload } from "mixpanel-browser";
import mixpanel from "mixpanel-browser";
import { ADMIN_BASE_URL } from "../../../constants/env";
import { getEnvName } from "../../../legacy/utils/getEnvName";
import { flushPendingIseePaywallTestAssignment } from "../experiments/isee-paywall-test/assign-isee-paywall-test";
import { isQaAccountSessionActive } from "../../qa-accounts/qa-account-email";
import { isRecordingDeniedPath } from "../../session-recording/is-recording-denied-path";

const ANALYTICS_CONSENT_GRANTED = "C0002:1";
const ONETRUST_CONSENT_COOKIE = "OptanonConsent";

export function getRecordSessionsPercent(): number {
  if (isRecordingDeniedPath(window.location.pathname)) {
    return 0;
  }

  switch (getEnvName()) {
    case "production":
      return 50;
    case "staging":
      return 10;
    default:
      return 0;
  }
}

function readOptanonConsentCookieGroups(): string {
  const cookieMatch = document.cookie.match(new RegExp(`(?:^|;\\s*)${ONETRUST_CONSENT_COOKIE}=([^;]*)`));
  const consent = cookieMatch ? decodeURIComponent(cookieMatch[1]) : "";
  const groupsMatch = consent.match(/(?:^|&)groups=([^&]*)/);
  return groupsMatch ? groupsMatch[1] : "";
}

function hasAnalyticsConsent(): boolean {
  const consentGroups = window.OTExternalConsent?.groups ?? readOptanonConsentCookieGroups();
  return consentGroups.includes(ANALYTICS_CONSENT_GRANTED);
}

export function syncTrackingConsent(): void {
  if (isQaAccountSessionActive()) {
    mixpanel.opt_out_tracking();
    return;
  }

  if (hasAnalyticsConsent()) {
    mixpanel.opt_in_tracking();
    flushPendingIseePaywallTestAssignment();
  } else {
    mixpanel.opt_out_tracking();
  }
}

export function isAdminRoute(pathname: string): boolean {
  return pathname === ADMIN_BASE_URL || pathname.startsWith(`${ADMIN_BASE_URL}/`);
}

export function stopSessionRecordingOnAdminRoute(pathname: string): void {
  const canStopRecording = typeof mixpanel.stop_session_recording === "function";
  if (isAdminRoute(pathname) && canStopRecording) {
    mixpanel.stop_session_recording();
  }
}

export function dropTrackingEventsForAdminOrQaSession(event: BeforeSendHookPayload): BeforeSendHookPayload | null {
  if (isAdminRoute(window.location.pathname) || isQaAccountSessionActive()) {
    return null;
  }
  return event;
}

export function registerTrackingConsentSync(): void {
  const previousWrapper = window.OptanonWrapper;
  window.OptanonWrapper = function onConsentResolved() {
    previousWrapper?.();
    syncTrackingConsent();
  };
  syncTrackingConsent();
}
